import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { StoreChrome } from '@/types'

import Link from 'next/link'
import { ArrowLeft, ShoppingBag } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useCart } from '@/lib/cart'
import { useCartSync } from '@/lib/use-cart-sync'
import { shippingFor } from '@/lib/pricing'
import { toFaDigits } from '@/lib/format'
import { Seo } from '@/components/generals/seo'
import { buttonVariants } from '@/components/ui/button'
import Container from '@/components/store/container'
import CartLine from '@/components/store/cart-line'
import EmptyState from '@/components/generals/empty-state'
import FreeShippingMeter from '@/components/store/free-shipping-meter'
import { Amount } from '@/components/store/price'
import { loadStoreChrome } from '@/lib/server/page'

interface CartPageProps {
    chrome: StoreChrome
}

const CartPage: FC<CartPageProps> = ({ chrome }): ReactNode => {
    const { items, hydrated, count, subtotal, updateQuantity, removeItem } = useCart()
    const { syncing } = useCartSync()
    const settings = chrome.settings
    const shipping = shippingFor(settings, subtotal)
    const hasUnavailable = items.some(item => item.stock <= 0)

    return (
        <>
            <Seo title='سبد خرید' noIndex />

            <Container className='pt-8 sm:pt-12'>
                <h1 className='text-3xl font-black sm:text-4xl'>
                    سبد خرید
                </h1>

                {hydrated && items.length > 0 && (
                    <p className='mt-2 text-sm text-muted-foreground'>
                        {toFaDigits(count)} کالا
                        {syncing && ' · در حال به‌روزرسانی قیمت‌ها'}
                    </p>
                )}

                {!hydrated ? (
                    <div className='mt-10 grid gap-4'>
                        {[0, 1].map(index => (
                            <div key={index} className='h-36 animate-pulse rounded-2xl bg-muted' />
                        ))}
                    </div>
                ) : items.length === 0 ? (
                    <EmptyState
                        icon={ShoppingBag}
                        title='سبد خرید شما خالی است'
                        description='هنوز محصولی به سبد اضافه نکرده‌اید. از میان محصولات فروشگاه، قطعه دلخواهتان را پیدا کنید.'
                        action={(
                            <Link href='/shop' className={buttonVariants({ size: 'lg' })}>
                                مشاهده فروشگاه
                            </Link>
                        )}
                        className='mt-10'
                    />
                ) : (
                    <div className='mt-10 grid gap-10 lg:grid-cols-12 lg:gap-12'>
                        <ul className='divide-y divide-border rounded-2xl border border-border bg-card px-5 sm:px-6 lg:col-span-8'>
                            {items.map(item => (
                                <li key={item.key} className='py-6'>
                                    <CartLine
                                        item={item}
                                        onQuantityChange={quantity => updateQuantity(item.key, quantity)}
                                        onRemove={() => removeItem(item.key)}
                                    />
                                </li>
                            ))}
                        </ul>

                        <aside className='lg:col-span-4' aria-label='خلاصه سفارش'>
                            <div className='space-y-4 rounded-2xl border border-border bg-card p-6 lg:sticky lg:top-28'>
                                <h2 className='text-lg font-bold'>
                                    خلاصه سفارش
                                </h2>

                                <dl className='space-y-3 text-sm'>
                                    <div className='flex items-center justify-between'>
                                        <dt className='text-muted-foreground'>
                                            جمع کالاها
                                        </dt>

                                        <dd>
                                            <Amount value={subtotal} />
                                        </dd>
                                    </div>

                                    <div className='flex items-center justify-between'>
                                        <dt className='text-muted-foreground'>
                                            هزینه ارسال
                                        </dt>

                                        <dd>
                                            {shipping === 0 ? <span className='font-bold text-success'>رایگان</span> : <Amount value={shipping} />}
                                        </dd>
                                    </div>

                                    <div className='flex items-center justify-between border-t border-border pt-3 text-base'>
                                        <dt className='font-bold'>
                                            مبلغ قابل پرداخت
                                        </dt>

                                        <dd>
                                            <Amount value={subtotal + shipping} className='text-lg' />
                                        </dd>
                                    </div>
                                </dl>

                                <FreeShippingMeter settings={settings} amount={subtotal} />

                                {hasUnavailable && (
                                    <p className='text-xs leading-6 text-destructive'>
                                        برخی اقلام ناموجود شده‌اند. برای ادامه، آن‌ها را از سبد حذف کنید.
                                    </p>
                                )}

                                <Link
                                    href='/checkout'
                                    aria-disabled={hasUnavailable}
                                    className={cn(buttonVariants({ size: 'lg' }), 'w-full', hasUnavailable && 'pointer-events-none opacity-50')}
                                >
                                    ادامه و ثبت سفارش
                                    <ArrowLeft className='size-4' aria-hidden='true' />
                                </Link>

                                <p className='text-center text-xs leading-6 text-muted-foreground'>
                                    کد تخفیف را در مرحله بعد وارد کنید.
                                </p>
                            </div>
                        </aside>
                    </div>
                )}
            </Container>
        </>
    )
}

export const getServerSideProps: GetServerSideProps<CartPageProps> = async () => {
    return { props: { chrome: await loadStoreChrome() } }
}

export default CartPage
